import { LocalizationService } from '@abp/ng.core';
import { inject, Injectable } from '@angular/core';
import { MenuItemCategory, MenuItemSubcategory } from '../interfaces/menu-items';
import { localizeWithFallback } from '../utils/localization-utils';

@Injectable({
  providedIn: 'root',
})
export class MenuTransformService {
  private readonly localization = inject(LocalizationService);

  translateItems(items: MenuItemCategory[]): MenuItemCategory[] {
    return items.map(item => ({
      ...item,
      title: localizeWithFallback(this.localization, item.title || ''),
      subcategories: item.subcategories.map(subcategory => ({
        ...subcategory,
        title: subcategory.title
          ? localizeWithFallback(this.localization, subcategory.title)
          : undefined,
        links: subcategory.links.map(link => ({
          ...link,
          title: localizeWithFallback(this.localization, link.title || ''),
        })),
      })),
    }));
  }

  sortAlphabetically(items: MenuItemCategory[]): MenuItemCategory[] {
    const currentLocale = this.localization.currentLang;
    const getSubcategorySortKey = (subcategory: MenuItemSubcategory) =>
      subcategory.title || subcategory.links[0]?.title || '';

    return items
      .map(category => ({
        ...category,
        subcategories: [...category.subcategories]
          .map(subcategory => ({
            ...subcategory,
            links: [...subcategory.links].sort((a, b) =>
              a.title.localeCompare(b.title, currentLocale)
            ),
          }))
          .sort((a, b) =>
            getSubcategorySortKey(a).localeCompare(getSubcategorySortKey(b), currentLocale)
          ),
      }))
      .sort((a, b) => a.title.localeCompare(b.title, currentLocale));
  }

  sortByTranslatedTitles(items: MenuItemCategory[]): MenuItemCategory[] {
    const currentLocale = this.localization.currentLang;
    const translate = (key?: string) => localizeWithFallback(this.localization, key || '');
    const getSubcategorySortKey = (subcategory: MenuItemSubcategory) =>
      translate(subcategory.title || subcategory.links[0]?.title);

    return items
      .map(category => ({
        ...category,
        subcategories: [...category.subcategories]
          .map(subcategory => ({
            ...subcategory,
            links: [...subcategory.links].sort((a, b) =>
              translate(a.title).localeCompare(translate(b.title), currentLocale)
            ),
          }))
          .sort((a, b) =>
            getSubcategorySortKey(a).localeCompare(getSubcategorySortKey(b), currentLocale)
          ),
      }))
      .sort((a, b) => translate(a.title).localeCompare(translate(b.title), currentLocale));
  }
}
